import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import keyBy from 'lodash/keyBy'
import sortBy from 'lodash/sortBy'
import { v4 as generateUuid } from 'uuid'

import api from '../../api'
import { filterDefinitions } from '../../filter-definitions'
import { SyncStatusValues } from '../../constants'
import readableColumnName from '../../utils/readable-column-name'
import { filterTitle } from '../../utils/filter-texts'
import { useInsightsRootStore } from '../insights-root'
import { useReconciliationTextsStore } from '../reconciliation-texts'
import { useAccountsListDrawer } from '../accounts-list-drawer'
import { useDrawer } from '@app/shared/pinia/drawer'
import I18n from '@app/adapters/I18n'

import type {
  ActiveFilter,
  ColumnName,
  FilterDefinition,
  DropdownItem,
  FilterDropdownValues,
} from './types'

export type {
  ActiveFilter,
  FilterDefinition,
  DropdownItem,
  FilterDropdownValues,
}

const emptyDropdownValues = (): FilterDropdownValues => ({
  company_template_list: [],
  company_type_list: [],
  country_list: [],
  currency_list: [],
  reporting_frequency_list: [],
  account_detail_template_list: [],
  sync_entity_list: [],
  sync_type_list: [],
  sync_name_list: [],
  sync_reference_list: [],
  administration_sync_type_list: [],
  administration_sync_name_list: [],
  administration_sync_reference_list: [],
  sync_status_list: [],
  firm_workflow_list: [],
  workflow_status_list: [],
  account_mapping_list_list: [],
  workflow_statuses: [],
  groups_list: [],
})

export const useFiltersStore = defineStore('insights-filters', () => {
  const insightsRootStore = useInsightsRootStore()
  const reconciliationTextsStore = useReconciliationTextsStore()

  const activeFilters = ref<ActiveFilter[]>([])
  const originalActiveFilters = ref<ActiveFilter[]>([])
  // The committed filters the results table currently reflects, captured when a
  // batch begins. While batching, edits are staged into activeFilters but the
  // table (and the menu-bar/column chrome derived from it) keeps showing this
  // baseline until Apply.
  const committedFilters = ref<ActiveFilter[]>([])
  const activeFilterPositions = ref<Record<string, number>>({})
  const activeFilterPositionsChanged = ref(false)
  const allFilters = ref<FilterDefinition[]>(
    filterDefinitions as FilterDefinition[]
  )
  const allFiltersByType = ref<Record<string, FilterDefinition>>(
    keyBy(filterDefinitions, 'type') as Record<string, FilterDefinition>
  )
  const editingFilter = ref<ActiveFilter | null>(null)
  // While true, the auto-apply watcher on activeFilters stays idle so a run of
  // filter edits inside the applied-filters overview modal can be committed in
  // one batch when the user hits Apply (rather than re-running per change).
  const isBatchingFilters = ref(false)
  const filterDropdownValuesLoading = ref(false)
  const filterDropdownValues = ref<FilterDropdownValues>(emptyDropdownValues())

  const getActiveFilters = computed((): ActiveFilter[] =>
    sortBy(activeFilters.value, (f) => activeFilterPositions.value[f.uuid] || 0)
  )

  // What the results table and its chrome show: the frozen baseline while
  // batching, otherwise the live working set.
  const displayedFilters = computed((): ActiveFilter[] =>
    isBatchingFilters.value ? committedFilters.value : getActiveFilters.value
  )

  const isActiveFiltersModified = computed(
    (): boolean =>
      !isEqual(activeFilters.value, originalActiveFilters.value) ||
      activeFilterPositionsChanged.value
  )

  const anyLedgerSpecificFiltersActive = computed((): boolean =>
    activeFilters.value.some(
      (filter) => allFiltersByType.value[filter.type]?.ledgerRequired
    )
  )

  const hasLedgerSpecificFiltersWithoutPeriodSelection = computed(
    (): boolean => {
      return (
        !insightsRootStore.periodSelection &&
        anyLedgerSpecificFiltersActive.value
      )
    }
  )

  const filterSettingsProperties = computed(() => ({
    filter: editingFilter.value,
  }))

  function getActiveFilterByUuid(uuid: string): ActiveFilter | undefined {
    return activeFilters.value.find((filter) => filter.uuid === uuid)
  }

  function getFilterByType(type: string): FilterDefinition {
    return allFilters.value.find(
      (filter) => filter.type === type
    ) as FilterDefinition
  }

  function getFilterDropdownValueLabel(type: string, value: unknown): unknown {
    const filterValueList = (
      filterDropdownValues.value as Record<string, DropdownItem[]>
    )[`${type}_list`]
    if (!filterValueList) return value
    if (filterValueList.length === 0) return ''
    // A value can outlive its dropdown entry (e.g. a removed filter type converted to a
    // company_type the enum no longer offers): render nothing rather than the string "undefined".
    return filterValueList.find((item) => item.value === value)?.label ?? ''
  }

  const filterGetters = computed(
    (): Record<string, unknown> => ({
      getActiveFilters: getActiveFilters.value,
      getFilterDropdownValueLabel,
      getReconciliationTextById: (id: number) =>
        reconciliationTextsStore.getReconciliationTextById(id),
    })
  )

  // Accepts the minimal shape this needs, so both ActiveFilter and the lighter
  // InsightsFilter (table cells) can be passed without casting.
  function getFilterHeaderTitle(filter: {
    type: string
    settings: Record<string, unknown>
    columnName?: ColumnName
  }): string {
    const { columnName } = filter
    const filterType = getFilterByType(filter.type)

    const customFilterTitle: null = ((filterType?.getColumnTitle &&
      filterType.getColumnTitle(filter.settings, filterGetters.value)) ||
      null) as null

    const finalColumnName = readableColumnName(
      columnName,
      filterType?.nameKey,
      customFilterTitle
    ) as string

    if (finalColumnName !== '') {
      return finalColumnName
    }

    return filterTitle(filterType?.nameKey)
  }

  function activeFilterSubtitle(filter: ActiveFilter): string {
    return (
      allFiltersByType.value[filter.type]?.activeSubtitle?.(
        filter.settings,
        filterGetters.value
      ) ?? ''
    )
  }

  function addActiveFilter(filter: ActiveFilter): void {
    activeFilters.value.push(filter)
  }

  function updateActiveFilter({
    filter,
    settings,
    columnName,
  }: {
    filter: ActiveFilter
    settings: Record<string, unknown>
    columnName?: ColumnName
  }): void {
    filter.settings = settings
    filter.columnName = columnName
  }

  function removeActiveFilterFromList(removeFilter: ActiveFilter): void {
    const filterIndex = activeFilters.value.findIndex(
      (filter) => filter.uuid === removeFilter.uuid
    )
    if (filterIndex !== -1) {
      activeFilters.value.splice(filterIndex, 1)
    }
    closeAccountsDrawerForRemovedColumn(removeFilter)
  }

  // When the column the accounts-list drawer is showing gets removed, its data
  // no longer has a home, so close the drawer. Stores are resolved lazily here
  // to avoid pulling them into this store's setup (circular-init safety).
  function closeAccountsDrawerForRemovedColumn(
    removeFilter: ActiveFilter
  ): void {
    const drawerColumnName = useAccountsListDrawer().columnName
    if (!drawerColumnName) return
    if (getFilterHeaderTitle(removeFilter) !== drawerColumnName) return

    // The drawer's onUnmounted hook calls clear() once it closes.
    useDrawer().closeRightDrawerWithoutPersisting(false)
  }

  function resetActiveFilterPositions(): void {
    activeFilterPositions.value = {}
    activeFilters.value.forEach(
      (filter, index) => (activeFilterPositions.value[filter.uuid] = index)
    )
  }

  function setActiveFilters(filters: unknown[]): void {
    activeFilters.value = (filters as ActiveFilter[]).map((filter) => ({
      ...filter,
      uuid: filter.cached_uuid || generateUuid(),
    }))
    originalActiveFilters.value = cloneDeep(activeFilters.value)
    activeFilters.value.forEach(
      (filter, index) => (activeFilterPositions.value[filter.uuid] = index)
    )
    activeFilterPositionsChanged.value = false
    resetActiveFilterPositions()
  }

  function unsetActiveFilters(): void {
    activeFilters.value = []
    originalActiveFilters.value = []
    resetActiveFilterPositions()
  }

  // Replaces the active filters with a previously captured snapshot, preserving
  // each filter's uuid (unlike setActiveFilters, which regenerates them). Used
  // to rewind the table to an earlier state.
  function restoreActiveFilters(filters: ActiveFilter[]): void {
    activeFilters.value = cloneDeep(filters)
    resetActiveFilterPositions()
  }

  function setBatchingFilters(value: boolean): void {
    // Capture the committed baseline as the batch opens (false -> true) so the
    // table chrome can keep showing it while edits are staged.
    if (value && !isBatchingFilters.value) {
      committedFilters.value = cloneDeep(getActiveFilters.value)
    }
    isBatchingFilters.value = value
  }

  function removeActiveFilter(filter: ActiveFilter): void {
    removeActiveFilterFromList(filter)
    resetActiveFilterPositions()
  }

  function addOrUpdateFilter({
    uuid,
    filterType,
    settings,
    columnName,
  }: {
    uuid?: string
    filterType: string
    settings: Record<string, unknown>
    columnName?: ColumnName
  }): void {
    const existingFilter = uuid ? getActiveFilterByUuid(uuid) : undefined

    if (existingFilter) {
      updateActiveFilter({ filter: existingFilter, settings, columnName })
    } else {
      addActiveFilter({
        uuid: uuid || generateUuid(),
        type: filterType,
        settings,
        columnName,
      })
      resetActiveFilterPositions()
    }
  }

  function saveFilter({
    uuid,
    filterType,
    settings,
    columnName,
  }: {
    uuid?: string
    filterType: string
    settings: Record<string, unknown>
    columnName?: ColumnName
  }): void {
    const filterDef = getFilterByType(filterType)
    const normalizedSettings = filterDef?.normalizeSettings
      ? filterDef.normalizeSettings(settings)
      : settings

    addOrUpdateFilter({
      uuid,
      filterType,
      columnName,
      settings: normalizedSettings,
    })
  }

  function editFilter(filter: ActiveFilter | Record<string, unknown>): void {
    editingFilter.value = { ...filter } as ActiveFilter
  }

  function resetFilter(): void {
    editingFilter.value = null
  }

  function updateFilterPosition({
    uuid,
    position,
  }: {
    uuid: string
    position: number
  }): void {
    activeFilterPositions.value[uuid] = position
    activeFilterPositionsChanged.value = true
  }

  async function fetchFilterDropdownValues(): Promise<void> {
    filterDropdownValuesLoading.value = true
    const data = (await api.fetchFilterDropdownValues()) as {
      company_templates: { id: string | number; name: string }[]
      company_types: string[]
      countries: { code: string; name: string }[]
      currencies: string[]
      report_frequencies: string[]
      firm_workflows: {
        id: string | number
        name: string
        partner_template_id?: unknown
      }[]
      workflow_statuses: { id: string | number; name: string }[]
      account_mapping_lists: { id: string | number; name: string }[]
      account_detail_templates: {
        id: string | number
        name: string
        partner_template_id?: unknown
      }[]
      sync_entities: {
        id: string | number
        name: string
        display_name?: string
      }[]
      sync_types?: string[]
      sync_references?: string[]
      administration_sync_entities?: {
        id: string | number
        name: string
        display_name?: string
      }[]
      administration_sync_types?: string[]
      administration_sync_references?: string[]
      groups: { id: number; name: string }[]
    }

    filterDropdownValues.value = {
      company_template_list: data.company_templates.map((f) => ({
        value: f.id,
        label: f.name,
      })),
      company_type_list: data.company_types.map((f) => ({
        value: f,
        label: I18n.t(`company.company_types.${f}`),
      })),
      country_list: data.countries.map((f) => ({
        value: f.code,
        label: f.name,
      })),
      currency_list: data.currencies.map((f) => ({
        value: f,
        label: f,
      })),
      reporting_frequency_list: data.report_frequencies.map((f) => ({
        value: f,
        label: I18n.t(`company.${f}_ledgers_per_year`),
      })),
      firm_workflow_list: data.firm_workflows.map((f) => ({
        value: f.id,
        label: f.name,
        partnerTemplateId: f.partner_template_id,
      })),
      workflow_status_list: data.workflow_statuses.map((f) => ({
        value: f.id,
        label: f.name,
      })),
      account_mapping_list_list: data.account_mapping_lists.map((f) => ({
        value: f.id,
        label: f.name,
      })),
      account_detail_template_list: (data.account_detail_templates || []).map(
        (f) => ({
          value: f.id,
          label: f.name,
          partnerTemplateId: f.partner_template_id,
        })
      ),
      sync_entity_list: [
        { label: I18n.t('companies.sync_entity_info.manual'), value: -1 },
        ...data.sync_entities.map((f) => ({
          value: f.id,
          label: f.name,
        })),
      ],
      sync_type_list: (data.sync_types || []).map((f) => ({
        value: f,
        label: f,
      })),
      sync_name_list: data.sync_entities.map((f) => ({
        value: f.id,
        label: f.display_name || f.name,
      })),
      sync_reference_list: (data.sync_references || []).map((f) => ({
        value: f,
        label: f,
      })),
      administration_sync_type_list: (data.administration_sync_types || []).map(
        (f) => ({
          value: f,
          label: f,
        })
      ),
      administration_sync_name_list: (
        data.administration_sync_entities || []
      ).map((f) => ({
        value: f.id,
        label: f.display_name || f.name,
      })),
      administration_sync_reference_list: (
        data.administration_sync_references || []
      ).map((f) => ({
        value: f,
        label: f,
      })),
      sync_status_list: [
        SyncStatusValues.SYNCHRONISED,
        SyncStatusValues.FAILED,
        SyncStatusValues.CANCELED,
        SyncStatusValues.IN_PROGRESS,
        SyncStatusValues.NO_SYNC_DATA,
        SyncStatusValues.NOT_SYNCHRONISED,
      ].map((value) => ({
        value,
        label: I18n.t(
          `companies_overview.filters.syncs_information.status_values.${value}`
        ),
      })),
      groups_list: data.groups.map((group) => ({
        value: group.id,
        label: group.name,
      })),
      workflow_statuses: [],
    }

    filterDropdownValuesLoading.value = false
  }

  async function searchSyncEntities(query: string): Promise<void> {
    const entities = (await api.searchSyncEntities(query)) as {
      id: string | number
      name: string
      display_name?: string
    }[]
    filterDropdownValues.value = {
      ...filterDropdownValues.value,
      sync_name_list: entities.map((f) => ({
        value: f.id,
        label: f.display_name || f.name,
      })),
    }
  }

  async function searchSyncReferences(query: string): Promise<void> {
    const references = (await api.searchSyncReferences(query)) as string[]
    filterDropdownValues.value = {
      ...filterDropdownValues.value,
      sync_reference_list: references.map((f) => ({
        value: f,
        label: f,
      })),
    }
  }

  async function searchAdministrationSyncEntities(
    query: string
  ): Promise<void> {
    const entities = (await api.searchAdministrationSyncEntities(query)) as {
      id: string | number
      name: string
      display_name?: string
    }[]
    filterDropdownValues.value = {
      ...filterDropdownValues.value,
      administration_sync_name_list: entities.map((f) => ({
        value: f.id,
        label: f.display_name || f.name,
      })),
    }
  }

  async function searchAdministrationSyncReferences(
    query: string
  ): Promise<void> {
    const references = (await api.searchAdministrationSyncReferences(
      query
    )) as string[]
    filterDropdownValues.value = {
      ...filterDropdownValues.value,
      administration_sync_reference_list: references.map((f) => ({
        value: f,
        label: f,
      })),
    }
  }

  return {
    activeFilters,
    originalActiveFilters,
    committedFilters,
    activeFilterPositions,
    activeFilterPositionsChanged,
    allFilters,
    allFiltersByType,
    editingFilter,
    isBatchingFilters,
    filterDropdownValuesLoading,
    filterDropdownValues,
    getActiveFilters,
    displayedFilters,
    isActiveFiltersModified,
    anyLedgerSpecificFiltersActive,
    hasLedgerSpecificFiltersWithoutPeriodSelection,
    filterSettingsProperties,
    getActiveFilterByUuid,
    getFilterByType,
    getFilterDropdownValueLabel,
    getFilterHeaderTitle,
    activeFilterSubtitle,
    setActiveFilters,
    unsetActiveFilters,
    restoreActiveFilters,
    setBatchingFilters,
    removeActiveFilter,
    addOrUpdateFilter,
    saveFilter,
    editFilter,
    resetFilter,
    updateFilterPosition,
    fetchFilterDropdownValues,
    searchSyncEntities,
    searchSyncReferences,
    searchAdministrationSyncEntities,
    searchAdministrationSyncReferences,
  }
})
